/**
 * FayresListPage — /fayres (customer-facing).
 *
 * The outer loop of the Fayre → Supplier → Chat flow. Each card is the
 * whole tap target — "View Suppliers" opens the fayre detail where the
 * real conversion surface lives. We show supplier-count + dates as the
 * primary trust signals; aggregate reply-speed lives one screen deeper
 * so we don't noise up the list.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, CalendarBlank, Users, CaretRight, Storefront, Fire, MagnifyingGlass } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_EMOJI = {
  dj: '🎧', photographer: '📷', videographer: '🎥',
  photobooth: '📸', caterer: '🍽️',
  florist: '🌼', venue: '🏛️', cake_maker: '🎂',
  transport: '🚗', decorator: '🎨',
  wedding_planner: '✨', hair_makeup: '💅',
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
};

const formatTimeRange = (s, e) => (s && e ? `${s}–${e}` : s || '');

const FayresListPage = () => {
  const navigate = useNavigate();
  const [fayres, setFayres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all'); // 'all' | 'this_week' | 'this_month'
  const [cityQuery, setCityQuery] = useState('');

  useEffect(() => {
    axios.get(`${API}/fayres`)
      .then((r) => setFayres(r.data.fayres || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const now = Date.now();
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const monthMs = 30 * 24 * 60 * 60 * 1000;
    return fayres.filter((f) => {
      if (cityQuery.trim()) {
        const c = (f.location?.city || f.location?.venue || '').toLowerCase();
        if (!c.includes(cityQuery.trim().toLowerCase())) return false;
      }
      if (dateFilter !== 'all' && f.date) {
        try {
          const dt = new Date(f.date + 'T00:00:00Z').getTime();
          const diff = dt - now;
          if (dateFilter === 'this_week' && (diff < 0 || diff > weekMs)) return false;
          if (dateFilter === 'this_month' && (diff < 0 || diff > monthMs)) return false;
        } catch {}
      }
      return true;
    });
  }, [fayres, dateFilter, cityQuery]);

  return (
    <div className="min-h-screen bg-[#0F1014] text-white pb-16" data-testid="fayres-list-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0F1014]/95 backdrop-blur border-b border-white/5 px-5 py-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-[10px] font-black uppercase tracking-[3px]" style={{ color: '#D4AF5E', fontFamily: 'Georgia, "Times New Roman", serif', letterSpacing: '0.22em' }}>EventDrop ✦</p>
          <h1 className="text-3xl md:text-4xl font-black mt-1">Wedding Fayres Near You</h1>
          <p className="text-white/50 text-sm mt-1">Meet suppliers · get quotes · book faster</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-8">
        {/* Filters — date pills + city search. Filter client-side
           because the list is small. Server-side filtering is overkill
           when we cap the published feed at a few dozen items. */}
        {!loading && fayres.length > 0 && (
          <div className="mb-6 space-y-3" data-testid="fayres-filters">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {[
                { key: 'all', label: 'All upcoming' },
                { key: 'this_week', label: '🔥 This week' },
                { key: 'this_month', label: 'This month' },
              ].map((p) => (
                <button
                  key={p.key}
                  onClick={() => setDateFilter(p.key)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-black border transition-all ${
                    dateFilter === p.key
                      ? 'bg-[#B89762]/40/20 text-[#F4E9D2] border-[#B89762]/45/50'
                      : 'bg-white/[0.04] text-white/60 border-white/10 hover:bg-white/[0.08]'
                  }`}
                  data-testid={`fayres-filter-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={cityQuery}
                onChange={(e) => setCityQuery(e.target.value)}
                placeholder="Filter by city or venue…"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-white placeholder:text-white/35 focus:border-[#B89762]/40/40 outline-none"
                data-testid="fayres-city-search"
              />
            </div>
          </div>
        )}

        {loading && (
          <p className="text-white/40 text-sm text-center py-12" data-testid="fayres-loading">Loading fayres…</p>
        )}
        {!loading && !filtered.length && fayres.length > 0 && (
          <div className="text-center py-16 rounded-2xl bg-white/[0.02] border border-white/5" data-testid="fayres-no-match">
            <Storefront size={32} weight="fill" className="mx-auto mb-2 text-white/20" />
            <p className="font-bold text-white/70">No fayres match your filters</p>
            <p className="text-white/40 text-sm mt-1">Try widening the date range or clearing the city search.</p>
          </div>
        )}
        {!loading && !fayres.length && (
          <div className="text-center py-16 rounded-2xl bg-white/[0.02] border border-white/5" data-testid="fayres-empty">
            <Storefront size={32} weight="fill" className="mx-auto mb-2 text-white/20" />
            <p className="font-bold text-white/70">No fayres listed yet</p>
            <p className="text-white/40 text-sm mt-1">Check back soon — new events added weekly.</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((f) => {
            const thisWeek = (() => {
              if (!f.date) return false;
              try {
                const dt = new Date(f.date + 'T00:00:00Z').getTime();
                return dt - Date.now() < 7 * 24 * 60 * 60 * 1000;
              } catch { return false; }
            })();
            return (
            <button
              key={f.id}
              onClick={() => navigate(`/fayres/${f.id}`)}
              className="text-left rounded-2xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-white/25 transition-all overflow-hidden"
              data-testid={`fayre-card-${f.id}`}
            >
              {/* Image banner or gradient fallback */}
              <div
                className="w-full h-36 relative"
                style={{
                  background: f.image_url
                    ? `url(${f.image_url}) center/cover`
                    : 'linear-gradient(135deg, rgba(108,85,224,0.4), rgba(194,157,82,0.25))',
                }}
              >
                {!f.image_url && (
                  <div className="absolute inset-0 flex items-center justify-center text-5xl opacity-90">
                    🎪
                  </div>
                )}
                {thisWeek && (
                  <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-orange-500/90 text-white flex items-center gap-1" data-testid={`fayre-this-week-${f.id}`}>
                    <Fire size={10} weight="fill" /> This week
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent" />
                <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
                  <div>
                    <p className="font-black text-white text-lg leading-tight line-clamp-1 drop-shadow" data-testid={`fayre-name-${f.id}`}>
                      {f.name}
                    </p>
                  </div>
                </div>
              </div>
              {/* Body */}
              <div className="p-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/60">
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={11} weight="bold" /> {f.location.city || f.location.venue}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <CalendarBlank size={11} weight="bold" /> {formatDate(f.date)}
                  </span>
                  {f.start_time && (
                    <span className="text-white/40">· {formatTimeRange(f.start_time, f.end_time)}</span>
                  )}
                </div>
                <p className="text-sm text-white/80 mt-2 flex items-center gap-2">
                  <Users size={14} weight="fill" className="text-emerald-300" />
                  <span className="font-bold">{f.supplier_count} supplier{f.supplier_count === 1 ? '' : 's'} attending</span>
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(f.categories || []).slice(0, 5).map((c) => (
                    <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/[0.06] text-white/60">
                      {CATEGORY_EMOJI[c] || '🎯'} {c.replace('_', ' ')}
                    </span>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-[11px] text-[#C29D52] font-black uppercase tracking-wider flex items-center gap-1" data-testid={`fayre-cta-${f.id}`}>
                    View suppliers <CaretRight size={10} weight="bold" />
                  </p>
                </div>
              </div>
            </button>
            );
          })}
        </div>

        {/* Organiser entry — every fayre browser is a potential organiser */}
        <div
          className="mt-10 rounded-2xl p-5 text-center"
          style={{ background: 'linear-gradient(135deg, rgba(108,85,224,0.12), rgba(194,157,82,0.06))', border: '1px solid rgba(255,255,255,0.06)' }}
          data-testid="organiser-cta"
        >
          <p className="text-[10px] font-black uppercase tracking-[2px] text-[#C29D52]/80 mb-1">For organisers</p>
          <p className="font-black text-lg">Running a wedding fayre?</p>
          <p className="text-white/50 text-sm mt-1">List it for free — suppliers find it, couples message before the day.</p>
          <button
            onClick={() => navigate('/organiser/fayres')}
            className="mt-3 inline-flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-black text-white"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="organiser-cta-btn"
          >
            List your fayre <CaretRight size={11} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FayresListPage;
